// Top bar (M18): green camera mark + "Photo Booth", segmented tabs Photos / Ads / Plating, the Pro tools
// (Pro mode, Photos only), then quiet icon buttons on the right: Pro switch, AI, EN·ES, theme, Help (a menu
// with the help pages, keyboard shortcuts and About).
import { useEffect, useRef, useState } from 'react';
import { Camera, HelpCircle, Moon, Sparkles, Sun } from 'lucide-react';
import { useLang, useT } from '../i18n';
import { useLibrary } from '../state/library';
import { useUi } from '../state/ui';
import { openPhotos, openRecent } from '../io/open';
import { recentFolders, type RecentFolder } from '../io/fs';
import { MetadataDialog } from '../ui/dialogs/MetadataDialog';
import { useSimple } from '../state/simple';
import { useWorkspace } from '../state/workspace';
import s from './Shell.module.css';
import { useTheme } from './theme';
import { makeAdFromPhotos } from '../design/ad/actions';
import { useAiUi } from '../features/ai/aiActions';
import { useAi } from '../features/ai/aiSettings';

function HelpMenu() {
  const t = useT();
  const lang = useLang((l) => l.lang);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    window.addEventListener('pointerdown', close);
    return () => window.removeEventListener('pointerdown', close);
  }, [open]);
  const pick = (fn: () => void) => () => {
    setOpen(false);
    fn();
  };
  return (
    <span ref={ref} style={{ position: 'relative' }}>
      <button
        className={s.icon}
        data-testid="open-help"
        aria-label={t('help')}
        title={t('help')}
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <HelpCircle className="icon" aria-hidden="true" />
      </button>
      {open && (
        <ul className={`${s.menu} ${s.menuRight}`} role="menu">
          <li>
            <button
              role="menuitem"
              data-testid="help-pages"
              onClick={pick(() => window.open(`/help/${lang}/index.html`, '_blank', 'noopener'))}
            >
              {t('helpPages')}
            </button>
          </li>
          <li>
            <button role="menuitem" onClick={pick(() => useUi.getState().set({ cheatOpen: true }))}>
              {t('cheatTitle')}
            </button>
          </li>
          <li>
            <button
              role="menuitem"
              data-testid="open-about"
              onClick={pick(() => useUi.getState().set({ aboutOpen: true }))}
            >
              {t('about')}
            </button>
          </li>
        </ul>
      )}
    </span>
  );
}

export function TopBar() {
  const t = useT();
  const [metaOpen, setMetaOpen] = useState(false);
  const mode = useSimple((st) => st.mode);
  const { lang, setLang } = useLang();
  const folder = useLibrary((st) => st.folderName);
  const count = useLibrary((st) => st.photos.length);
  const ui = useUi();
  const [recent, setRecent] = useState<RecentFolder[] | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  // Simple mode shows only what a first-time user needs (CODE_REVIEW S18): the Pro tools (folders, zoom,
  // split, clipping, cull, the full export dialog, metadata) stay behind the Pro switch.
  const pro = mode === 'pro';
  const ws = useWorkspace((st) => st.ws);
  const designEnabled = useWorkspace((st) => st.designEnabled);
  const photoTools = pro && ws === 'photos';
  const aiOn = useAi((a) => !!a.settings?.key);
  const theme = useTheme((x) => x.theme);
  return (
    <header className={s.top}>
      <span className={s.title}>
        <span className={s.mark} aria-hidden="true">
          <Camera className="icon" />
        </span>
        {t('appShort')}
      </span>
      <div className={s.tabs} role="group" aria-label={t('workspace')}>
        <button
          aria-pressed={ws === 'photos'}
          data-testid="ws-photos"
          onClick={() => useWorkspace.getState().setWs('photos')}
        >
          {t('wsPhotos')}
        </button>
        {designEnabled && (
          <button
            aria-pressed={ws === 'design'}
            data-testid="ws-design"
            onClick={() => useWorkspace.getState().setWs('design')}
          >
            {t('wsDesign')}
          </button>
        )}
        <button
          aria-pressed={ws === 'plating'}
          data-testid="ws-plating"
          onClick={() => useWorkspace.getState().setWs('plating')}
        >
          {t('wsPlating')}
        </button>
      </div>
      {pro && ws === 'photos' && (
        <>
          <button onClick={() => void openPhotos()} data-testid="open-folder">
            {t('openFolder')}
          </button>
          <span style={{ position: 'relative' }}>
            <button
              aria-expanded={!!recent}
              onClick={async () => setRecent(recent ? null : await recentFolders())}
            >
              {t('recent')}
            </button>
            {recent && (
              <ul className={s.menu} role="menu">
                {recent.length === 0 && <li className={s.menuEmpty}>{t('recentEmpty')}</li>}
                {recent.map((r) => (
                  <li key={r.name + r.openedAt}>
                    <button
                      role="menuitem"
                      onClick={async () => {
                        setRecent(null);
                        if (!(await openRecent(r))) setMsg(t('permissionNeeded', { name: r.name }));
                      }}
                    >
                      {r.name}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </span>
          <span className={s.folder}>
            {count
              ? `${folder ?? t('openFiles')} · ${t('photoCount', { n: count })}`
              : (msg ?? t('noFolder'))}
          </span>
        </>
      )}
      {photoTools && (
        <>
          <button
            aria-pressed={ui.zoom === 1}
            onClick={() => ui.set({ zoom: ui.zoom === 1 ? 'fit' : 1 })}
            title="Z"
          >
            {ui.zoom === 1 ? t('zoomFit') : t('zoom100')}
          </button>
          <button aria-pressed={ui.split} onClick={() => ui.set({ split: !ui.split })} title="Y">
            {t('split')}
          </button>
          <button
            aria-pressed={ui.clipping}
            onClick={() => ui.set({ clipping: !ui.clipping })}
            title="J"
            data-testid="clipping"
          >
            {t('clipping')}
          </button>
          <button disabled={!count} onClick={() => useLibrary.getState().set({ cull: true })} title="E / F">
            {t('cullView')}
          </button>
          <button
            disabled={!count}
            onClick={() => ui.set({ exportOpen: true })}
            title="Ctrl+E"
            data-testid="open-export"
          >
            {t('export')}
          </button>
          <button onClick={() => setMetaOpen(true)} data-testid="open-metadata">
            {t('metadataButton')}
          </button>
          {designEnabled && (
            <button onClick={makeAdFromPhotos} data-testid="topbar-make-ad" title={t('makeAdHint')}>
              {t('makeAd')}
            </button>
          )}
        </>
      )}
      {metaOpen && <MetadataDialog onClose={() => setMetaOpen(false)} />}
      <span className={s.spacer} />
      <label className={s.proSwitch}>
        <input
          type="checkbox"
          role="switch"
          data-testid="pro-mode"
          checked={mode === 'pro'}
          onChange={(e) => useSimple.getState().setMode(e.target.checked ? 'pro' : 'simple')}
        />
        {t('proMode')}
      </label>
      <button
        className={`${s.icon} ${aiOn ? s.iconOn : ''}`}
        data-testid="open-ai"
        onClick={() => useAiUi.setState({ settingsOpen: true })}
        title={t('aiSettings')}
        aria-label={t('aiSettings')}
      >
        <Sparkles className="icon" aria-hidden="true" />
      </button>
      <div className={s.langToggle} role="group" aria-label={t('language')}>
        <button aria-pressed={lang === 'en'} onClick={() => setLang('en')}>
          EN
        </button>
        <button aria-pressed={lang === 'es'} onClick={() => setLang('es')}>
          ES
        </button>
      </div>
      <button
        className={s.icon}
        data-testid="theme-toggle"
        aria-label={t(theme === 'dark' ? 'themeLight' : 'themeDark')}
        title={t(theme === 'dark' ? 'themeLight' : 'themeDark')}
        onClick={() => useTheme.getState().setTheme(theme === 'dark' ? 'light' : 'dark')}
      >
        {theme === 'dark' ? (
          <Sun className="icon" aria-hidden="true" />
        ) : (
          <Moon className="icon" aria-hidden="true" />
        )}
      </button>
      <HelpMenu />
    </header>
  );
}
